import React from 'react'

import { useForTablet } from '@sketch/components'
import { getVersionRenderStatus } from '@sketch/modules-common'
import { VersionFragment, useGetSectionsForPageQuery } from '@sketch/gql-types'

import { ShareWithoutVersion } from '../../../../versioning'
import { DocumentSidebarLayoutExtraProps } from '../../../components/DocumentSidebarLayout'
import { Panel } from '../../../components/Panel'
import { FloatingZoom } from '../../../FrameView/components/FloatingZoom'
import { SharePageCanvas } from '../../../PageCanvasView/components/SharePageCanvas'
import { PageCanvasSupportGate } from '../../../PageCanvasView/components/PageCanvasSupportGate'
import ShareReadyGuard from '../../../components/ShareReadyGuard'
import { StyledCanvasViewWrapper } from '../../../PageCanvasView/PageCanvasView.styles'
import { CanvasLeftPanel } from '../../../../canvas/components/CanvasLeftPanel'
import { DraftAnnotationNotifier } from '../../../../annotations/components'
import { SectionSelector } from '../SectionSelector'
import { SectionDetailHeader } from '../SectionDetailHeader'

interface SectionViewProps {
  layoutProps: DocumentSidebarLayoutExtraProps
  share: ShareWithoutVersion
  currentVersion: VersionFragment | null
  pageUUID: string
  sectionUUID: string
  isViewingLatestVersion: boolean
  renderStatus: ReturnType<typeof getVersionRenderStatus>
  isInspectPanelOpen: boolean
}

/**
 * Layout for the single-Section focus view - the Section equivalent of
 * FrameView. Reuses SharePageCanvas, which isolates the Section by making it the
 * renderer's scene root (via the rootEntityUUID prop passed down to the canvas),
 * so the layout is otherwise identical to the plain page view.
 */
export const SectionView = ({
  layoutProps,
  share,
  currentVersion,
  pageUUID,
  sectionUUID,
  isViewingLatestVersion,
  renderStatus,
  isInspectPanelOpen,
}: SectionViewProps) => {
  const {
    HeaderPortal,
    SidebarRightPortal,
    toggleSidebarRight,
    isSidebarRightOpen,
  } = layoutProps
  const isTabletAndBigger = useForTablet()

  // GQL-backed, so this resolves independently of the WASM engine parsing
  // the presentation file - the header can populate as soon as the network
  // responds, instead of waiting on usePRFileSections().
  const { data } = useGetSectionsForPageQuery({
    variables: {
      identifier: share.identifier,
      versionShortId: currentVersion?.shortId ?? '',
      pageUUID,
    },
    skip: !currentVersion?.shortId,
  })
  const page = data?.share?.version?.document?.page
  const currentSection = page?.sections.find(
    section => section.uuid === sectionUUID
  )

  // Always defined from data we already have synchronously (sectionUUID and
  // pageUUID both come from the URL) so the header renders immediately on
  // navigation instead of staying empty - name/pageName fill in once the
  // GQL query resolves, in place, without remounting anything.
  const sectionInfo = {
    uuid: sectionUUID,
    name: currentSection?.name ?? '',
    pageUUID,
    pageName: page?.name ?? '',
  }

  return (
    <>
      <HeaderPortal>
        <SectionDetailHeader
          share={share}
          sectionInfo={sectionInfo}
          toggleSidebarRight={toggleSidebarRight}
          isSidebarRightOpen={isSidebarRightOpen}
          isViewingLatestVersion={isViewingLatestVersion}
          userCanOpenInApp={!!currentVersion?.document?.userCanOpenInApp}
          currentVersion={currentVersion}
        />

        <Panel
          share={share}
          version={currentVersion || undefined}
          userCanSeeComments
          userCanInspect={share.userCanInspect}
          SidebarRightPortal={SidebarRightPortal}
        />

        <FloatingZoom />
      </HeaderPortal>

      <PageCanvasSupportGate>
        <ShareReadyGuard renderStatus={renderStatus}>
          <StyledCanvasViewWrapper>
            <CanvasLeftPanel
              isInspectPanelOpen={isInspectPanelOpen}
              stateKey={`${share.identifier}:${pageUUID}`}
              navigationComponent={
                <SectionSelector
                  shareIdentifier={share.identifier}
                  pageUUID={pageUUID}
                  currentSectionUUID={sectionUUID}
                  currentSectionName={currentSection?.name}
                />
              }
            />
            <SharePageCanvas
              isViewingLatestVersion={isViewingLatestVersion}
              permanentPageIdentifier={pageUUID}
              rootEntityUUID={sectionUUID}
            />
            {isTabletAndBigger && <DraftAnnotationNotifier />}
          </StyledCanvasViewWrapper>
        </ShareReadyGuard>
      </PageCanvasSupportGate>
    </>
  )
}
